import { cn, generateId } from "@/lib/utils";
import { ChatMessageData } from "@/services/promptEditor.service";
import Button from "@components/library/Button";
import IconButton from "@components/library/IconButton";
import { PlayIcon } from "@components/library/Icons";
import { CmdEnterShortcut } from "@components/library/KeyboardShortcut";
import { Tooltip } from "@components/library/Tooltip";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { PlusIcon, XIcon } from "@heroicons/react/outline";
import { castDraft } from "immer";
import { Dispatch, memo, SetStateAction, useMemo, useState } from "react";
import { ChatMessageRole } from "../../../services/promptEditor.service";
import { ChatDatum, DatumStatus } from "../PlaygroundData";
import { Message } from "./Message";
import { NO_OP_HANDLER } from "./MessageEditor";
import { ArrowBendLeftUp, Check } from "@phosphor-icons/react";

interface Props {
  status: DatumStatus | null;
  messages: ChatDatum["messages"];
  setMessages?: Dispatch<SetStateAction<ChatMessageData[]>>;
  updateToolMessage?: (toolCallId: string, output: string) => void;
  inTemplate?: boolean;
  onRun?: () => void;
  onCancel?: () => void;
  handleSave?: () => void;
  hasToolCalling?: boolean;
  isOutput?: boolean;
  onAddOutputMessagesToMessages?: () => void;
}

// TODO: This could be refactored as right now it used by agent, chat and has allowance to be used in LHS in the Editor.
/** The editor for chat messages, both in the main editor and in the template editor */
export const ChatMessagesEditor = memo(
  ({
    status,
    messages,
    setMessages,
    updateToolMessage,
    inTemplate,
    onRun,
    onAddOutputMessagesToMessages,
    onCancel,
    handleSave,
    hasToolCalling: hasToolCalling,
    isOutput = false,
  }: Props) => {
    const [lastTemplateRole, setLastTemplateRole] = useState<ChatMessageRole>("user");
    const writableMessages = castDraft(messages); // castDraft is a no-op, just makes TypeScript happy.
    const [animationParent, enableAnimations] = useAutoAnimate({
      duration: 150, // Default is 250, but we're faster.
    });

    // Separate tool messages that are referenced by tool calls by the assistant.
    // (These are shown in the assistant message.)
    // This is usually all tool messages, unless the user has messed something up.
    // We need to do this accounting to make sure we're not hiding tool messages.
    const [toolMessagesForAssistantToolCalls, displayedMessages] = useMemo(() => {
      const assistantToolCallIds = new Set<string>();
      messages.forEach((message) => {
        if (message.role === "assistant") {
          message.tool_calls?.forEach((toolCall) => {
            assistantToolCallIds.add(toolCall.id);
          });
        }
      });
      const toolMessagesForAssistantToolCalls: ChatMessageData[] = [];
      const displayedMessages: { message: ChatMessageData; index: number }[] = []; // Ensure we keep the original index for calling setMessages later.
      messages.forEach((message, index) => {
        if (message.role === "tool" && message.tool_call_id && assistantToolCallIds.has(message.tool_call_id)) {
          toolMessagesForAssistantToolCalls.push(message);
        } else {
          displayedMessages.push({ message, index });
        }
      });
      return [toolMessagesForAssistantToolCalls, displayedMessages];
    }, [messages]);

    return (
      <div className="group/messages flex w-full flex-col pb-24">
        <div className="flex w-full flex-col" ref={animationParent}>
          {/* <pre className="whitespace-pre-wrap text-10-10">{JSON.stringify(messages, null, 2)}</pre> */}
          {displayedMessages.map(({ message, index }) => (
            // TODO: Make sure all messages get an ID.
            <div key={message._id || index} className="relative">
              <Message
                message={message}
                setMessage={
                  setMessages
                    ? (message) => {
                        if (!("_id" in message)) {
                          console.log("message has no _id", message, index);
                        }
                        // First match message by ID, then by index.
                        setMessages((messages) => {
                          let foundMessage = false;
                          let newMessages = messages.map((m, j) => {
                            if (m._id && m._id === message._id) {
                              foundMessage = true;
                              return message;
                            }
                            return m;
                          });
                          if (!foundMessage) {
                            // If message ID not found, match by index.
                            console.log("setting message by index", message, index);
                            newMessages = messages.map((m, j) => (index === j ? message : m));
                          }
                          return newMessages;
                        });
                      }
                    : undefined
                }
                onRemove={
                  setMessages
                    ? () => {
                        setMessages((messages) => {
                          // First match message by ID, then by index.
                          let foundMessage = false;
                          let newMessages = messages.filter((m, j) => {
                            if (m._id && m._id === message._id) {
                              foundMessage = true;
                              return false;
                            }
                            return true;
                          });
                          if (!foundMessage) {
                            // If message ID not found, match by index.
                            console.log("removing message by index", message, index);
                            newMessages = messages.filter((m, j) => index !== j);
                          }
                          return newMessages;
                        });
                      }
                    : undefined
                }
                onRun={onRun}
                handleSave={handleSave ?? NO_OP_HANDLER}
                inTemplate={inTemplate}
                hasToolCalling={hasToolCalling}
                toolMessages={toolMessagesForAssistantToolCalls}
                updateToolMessage={updateToolMessage}
              />
              {/* Insert new message button visible on separator hover, tho not on the last message */}
              {setMessages && index !== messages.length - 1 && (
                <div className="group absolute -bottom-[5px] z-[1] flex h-20 w-full items-center justify-center text-center">
                  <hr className="absolute h-1 w-full max-w-screen-lg border-none bg-gradient-to-r from-transparent via-gray-150 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  <Tooltip content="Insert message">
                    <IconButton
                      size={20}
                      shade="black"
                      className="bg-white opacity-0 transition-opacity group-hover:opacity-100"
                      styling="outline"
                      Icon={PlusIcon}
                      onClick={() => {
                        // Add a new message at index i
                        const prevTemplateRole = messages && messages.length > 0 ? messages[index].role : "system";
                        const newMessage = createChatMessage({ role: getNextRole(prevTemplateRole, inTemplate) });
                        setMessages([
                          ...writableMessages.slice(0, index + 1),
                          newMessage,
                          ...writableMessages.slice(index + 1),
                        ]);
                        focusOnMessageWithIdAfterRerender(newMessage._id!);
                      }}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          ))}
          <div className="h-20" />
        </div>
        <div className={cn("mx-auto flex w-full max-w-screen-lg gap-12 px-20")}>
          {!isOutput && (
            <Button
              IconLeft={PlusIcon}
              shade="gray"
              disabled={!setMessages}
              onClick={() => {
                const prevTemplateRole =
                  messages && messages.length > 0 ? messages[messages.length - 1].role : "system";
                const newMessage = createChatMessage({ role: getNextRole(prevTemplateRole, inTemplate) });
                if (inTemplate) {
                  setLastTemplateRole(lastTemplateRole === "user" ? "assistant" : "user");
                }
                setMessages && setMessages((messages) => [...messages, newMessage]);
                focusOnMessageWithIdAfterRerender(newMessage._id!);
              }}
              size={24}
            >
              Message
            </Button>
          )}

          {onRun && !onCancel && isOutput && (
            <Tooltip
              content={
                <div className="flex items-center gap-4 ">
                  <CmdEnterShortcut />
                </div>
              }
            >
              <Button IconLeft={status === "suspended" ? Check : PlayIcon} onClick={onRun} size={24} shade="blue">
                {status === "suspended" ? "Continue" : messages.length > 0 ? "Clear & Run" : "Run"}
              </Button>
            </Tooltip>
          )}
          {onCancel && (
            <Button IconLeft={XIcon} size={24} onClick={onCancel} shade="blue">
              Stop
            </Button>
          )}
          {isOutput && messages.length > 0 && onAddOutputMessagesToMessages && status === "complete" && (
            <Button IconLeft={ArrowBendLeftUp} onClick={onAddOutputMessagesToMessages} size={24}>
              Add to Messages
            </Button>
          )}
        </div>
      </div>
    );
  },
);

ChatMessagesEditor.displayName = "ChatMessagesEditor";

export const getNextRole = (role: ChatMessageRole, inTemplate?: boolean): ChatMessageRole => {
  // UX convenience, that we should alternative between users and assistants
  switch (role) {
    case "user":
      return "assistant";
    case "assistant":
      return "user";
    default:
      return inTemplate ? "system" : "user";
  }
};

export const createChatMessage = (message?: Partial<ChatMessageData>): ChatMessageData => {
  return {
    _id: generateId("message"),
    role: "user",
    content: "",
    ...(message || {}),
  };
};

export const createToolMessage = (name: string, tool_call_id: string): ChatMessageData => {
  return createChatMessage({
    _id: generateId("message"),
    role: "tool",
    content: "",
    name,
    tool_call_id,
  });
};

const focusOnMessageWithId = (messageId: string): void => {
  const element = document.querySelector(`[data-messageid="${messageId}"] .public-DraftEditor-content`);
  if (element) {
    (element as any).focus();
  } else {
    console.log("Couldn't find element to focus on", messageId);
  }
};

export const focusOnMessageWithIdAfterRerender = (messageId: string): void => {
  setTimeout(() => {
    focusOnMessageWithId(messageId);
  }, 50);
};
